/**
 * Vorlage „Rühren“.
 *
 * Ein Kochlöffel zieht gleichmäßige Kreise durch eine Masse in der Schüssel
 * oder im Topf. Eine Spirale auf der Oberfläche zeigt die Bewegung.
 */
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { smooth } from '../paths.ts';
import { compact, shape } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { arcArrow, counter } from '../parts/stage.ts';
import { GRATE_Y, stove, stoveKnob } from '../parts/stove.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { bowl, contents, pot } from '../parts/vessels.ts';
import type { Vessel } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Gefäße. */
const BOWL = { cx: 112, rim: 80, width: 140, height: 54 } as const;
const POT = { cx: 108, width: 124, height: 46 } as const;
const LEVEL = 0.2;
/** Umläufe des Löffels je Schleife. */
const TURNS = 2;

/** Spirale auf der Oberfläche, gedreht um angle (Bogenmaß). */
function swirl(cx: number, cy: number, rx: number, ry: number, angle: number): SceneNode {
  const points: Point[] = [];
  for (let index = 0; index <= 36; index += 1) {
    const f = index / 36;
    const r = 0.12 + 0.68 * f;
    const a = angle + f * Math.PI * 2.4;
    points.push({ x: cx + Math.cos(a) * rx * r, y: cy + Math.sin(a) * ry * r });
  }
  return shape(smooth(points, false), { stroke: 'deep', strokeWidth: STROKE.detail });
}

/** Vorlage „Rühren“. */
export const mixStirTemplate = defineTemplate({
  id: 'mix.stir',
  group: 'mix',
  title: 'Rühren',
  description:
    'Ein Kochlöffel wird in gleichmäßigen Kreisen durch die Masse gezogen, damit sich alles verbindet und nichts ansetzt.',
  durationSeconds: 4,
  keywords: [
    'rühren',
    'umrühren',
    'verrühren',
    'glatt rühren',
    'unterrühren',
    'einrühren',
    'gelegentlich rühren',
  ],
  stillTime: 0.1,
  parameters: [choice('vessel', 'Gefäß', { bowl: 'Schüssel', pot: 'Topf auf dem Herd' }, 'bowl')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/topf|herd|köcheln|kochen/.test(lower)) return { vessel: 'pot' };
    if (/schüssel/.test(lower)) return { vessel: 'bowl' };
    return {};
  },
  scene(values, t) {
    const inPot = textValue(values, 'vessel', 'bowl') === 'pot';
    const vessel: Vessel = inPot
      ? pot(POT.cx, GRATE_Y - POT.height, POT.width, POT.height)
      : bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const surfaceY = vessel.rimY + vessel.depth * LEVEL;
    const rx = vessel.widthAt(LEVEL);
    const ry = vessel.ry * (rx / vessel.rx);
    const angle = t * TURNS * Math.PI * 2;
    const mass = contents(vessel, LEVEL, inPot ? 'shade' : 'tint', [
      swirl(vessel.cx, surfaceY, rx, ry, angle),
    ]);
    const spoon = place(
      vessel.cx + Math.cos(angle) * rx * 0.48,
      surfaceY + 3 + Math.sin(angle) * ry * 0.48,
      -10 + Math.cos(angle) * 12,
      woodenSpoon(),
      0.85,
    );
    const burner = inPot ? stove(POT.cx, 0.35, t) : null;
    return compact([
      ...counter(),
      ...(burner?.back ?? []),
      ...vessel.back,
      mass,
      spoon,
      ...vessel.front,
      ...(burner?.front ?? []),
      inPot ? stoveKnob(214, 0.35) : null,
    ]);
  },
  hints(values) {
    const inPot = textValue(values, 'vessel', 'bowl') === 'pot';
    const cx = inPot ? POT.cx : BOWL.cx;
    const rim = inPot ? GRATE_Y - POT.height : BOWL.rim;
    return [arcArrow(cx, rim - 4, 46, 11, 200, 340)];
  },
});
